import { useCallback, useEffect, useMemo } from "react";
import {
  defaultPageOf,
  pageToLayout,
  useDashboard,
  useDashboardMutations,
  useWidgetData,
  type DashboardKeyScope,
  type DashboardLayoutItem,
} from "@playbookai/dashboard-client";
import type { DashboardItem } from "@playbookai/ui";
import type {
  DashboardPageRecord,
  DashboardRecord,
  WidgetDataResolveResponse,
  WidgetKind,
  WidgetWidth,
} from "@playbookai/types";
import { MoodTodayOutputSchema } from "@playbookai/widget-schemas";
import { useAuthStore } from "../auth/auth-store.js";
import { availableModuleSet } from "../modules/availability.js";
import {
  isHomeWidgetAvailable,
  mergeHomeWidgetOrder,
} from "./widget-availability.js";
import {
  putMoodToday,
  putMoodVisibility,
  type MoodCheckWrite,
} from "../api/mood-api.js";
import {
  MAX_TIMEOUT_MS,
  localDateIn,
  msUntilNextLocalDay,
} from "./local-day-boundary.js";

/**
 * Personal Home over the LIVE dashboard capability (ADR-0054): configuration
 * persists in `dashboard.*` via the idempotent `ensure` + CAS mutations of the
 * shared client — the legacy `home_dashboard` placement rail is retired.
 * Exactly one page; widget content resolves through the batched widget-data
 * endpoint, separate from configuration.
 *
 * KPI selection (`kpiId`/`kpiIds`) is a DATA CONNECTION, not widget params:
 * `onEditWidget` splits the editor's draft — presentation params PATCH the
 * instance, the approved KPI choice PUTs (or, when the user clears it,
 * DELETEs) the slot connection (§11.4). The merged view (`params + connection
 * selection`) is rebuilt for the edit dialog, so the existing KPI pickers keep
 * working unchanged.
 *
 * The two writes are SERIALIZED, and each is skipped when it has nothing to
 * persist: they share one `expectedDashboardVersion`, so firing both at once
 * made the second write lose a CAS race against the first — one user, one
 * edit, guaranteed "your change wasn't saved".
 */

/** Slot selection keys (data-selecting → connection, never presentation). */
export const HOME_CONNECTION_SELECTION: Partial<
  Record<
    WidgetKind,
    {
      slotKey: string;
      paramKeys: readonly string[];
      emptyRemovesConnection?: boolean;
    }
  >
> = {
  singleKpi: {
    slotKey: "kpi",
    paramKeys: ["kpiId"],
    emptyRemovesConnection: true,
  },
  chart: {
    slotKey: "kpi",
    paramKeys: ["kpiId"],
    emptyRemovesConnection: true,
  },
  kpiWatchlist: {
    slotKey: "kpis",
    paramKeys: ["kpiIds"],
    emptyRemovesConnection: true,
  },
  kpiTable: {
    slotKey: "kpis",
    paramKeys: ["kpiIds"],
    emptyRemovesConnection: true,
  },
  kaizenTable: {
    slotKey: "kaizens",
    paramKeys: [
      "owner",
      "ownerIds",
      "stage",
      "planStatuses",
      "dueState",
      "vsmIds",
    ],
  },
  solveActions: {
    slotKey: "actions",
    // Every data-SELECTING key. `groupBy` and `columns` stay out: they are
    // presentation and belong on the instance (ADR-0054 §4).
    paramKeys: [
      "sources",
      "solveIds",
      "owners",
      "owner",
      "ownerIds",
      "containmentStatuses",
      "countermeasureStatuses",
      "dueState",
    ],
  },
};

/** Merge a slot connection's selection back into the item's editable params. */
function mergeSelectionIntoParams(
  page: DashboardPageRecord,
  item: DashboardLayoutItem,
): DashboardItem {
  const selection = HOME_CONNECTION_SELECTION[item.kind];
  if (!selection) return item as DashboardItem;
  const instance = page.widgetInstances.find((i) => i.id === item.instanceId);
  const connection = instance?.connections.find(
    (c) => c.slotKey === selection.slotKey,
  );
  const merged = { ...item.params } as Record<string, unknown>;
  for (const key of selection.paramKeys) {
    const selected = connection?.parameters[key];
    if (selected !== undefined) merged[key] = selected;
  }
  return {
    ...item,
    params: merged,
  } as DashboardItem;
}

/** An emptied picker: the user un-picked everything in the slot. */
function isEmptySelection(value: unknown): boolean {
  return Array.isArray(value)
    ? value.length === 0
    : value === undefined || value === null || value === "";
}

/** Order-sensitive equality over the JSON-shaped values a picker produces. */
function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

export interface HomeDashboard {
  readonly layout: DashboardItem[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly retryDashboard: () => void;
  readonly record: DashboardRecord | undefined;
  readonly page: DashboardPageRecord | undefined;
  readonly widgetData: WidgetDataResolveResponse | undefined;
  readonly widgetDataLoading: boolean;
  readonly retryWidgetData: () => void;
  readonly onReorder: (next: DashboardItem[]) => Promise<void>;
  readonly onChangeWidth: (instanceId: string, width: WidgetWidth) => void;
  readonly onAddWidget: (kind: WidgetKind) => void;
  readonly onRemoveWidget: (instanceId: string) => void;
  readonly onEditWidget: (
    instanceId: string,
    next: { params?: Record<string, unknown>; title?: string | undefined },
  ) => void;
  readonly onLogMood: (entry: MoodCheckWrite) => void;
  readonly onSetMoodHidden: (hidden: boolean) => void;
}

export function useHomeDashboard(options?: {
  readonly onConflict?: () => void;
  /** Non-conflict failure (400/403/500, or a failed mood write). */
  readonly onError?: (error: unknown) => void;
}): HomeDashboard {
  const claims = useAuthStore((s) => s.claims);
  const authed = claims?.userId != null && claims?.tenantId != null;
  // ADR-0100: the installable modules this workspace may use, KPI Manager
  // included, so a KPI widget follows it like every other widget its module.
  const enabledModules = useMemo(
    () => availableModuleSet(claims?.enabledModules ?? []),
    [claims?.enabledModules],
  );
  const scope: DashboardKeyScope = useMemo(
    () => ({
      tenantId: claims?.tenantId ?? null,
      userId: claims?.userId ?? null,
    }),
    [claims?.tenantId, claims?.userId],
  );

  const dashboard = useDashboard({
    dashboardType: "personal_home",
    scope,
    enabled: authed,
  });
  const record = dashboard.record;
  const page = record ? defaultPageOf(record) : undefined;

  const onConflict = options?.onConflict;
  const onError = options?.onError;
  const mutations = useDashboardMutations({
    dashboard,
    ...(onConflict ? { onConflict: () => onConflict() } : {}),
    ...(onError ? { onError } : {}),
  });

  const rawLayout = useMemo(() => (page ? pageToLayout(page) : []), [page]);
  const layout = useMemo(
    () =>
      page
        ? rawLayout
            .filter((item) => isHomeWidgetAvailable(item.kind, enabledModules))
            .map((item) => mergeSelectionIntoParams(page, item))
        : [],
    [page, rawLayout, enabledModules],
  );

  const widgetInstanceIds = useMemo(
    // Quick Access is host-context-only: it has no data connection and must
    // not create a meaningless resolver request.
    () =>
      layout
        .filter((item) => item.kind !== "quickAccess")
        .map((item) => item.instanceId),
    [layout],
  );
  const widgetDataQuery = useWidgetData({
    scope,
    dashboardId: record?.id,
    pageId: page?.id,
    widgetInstanceIds,
    enabled: authed && enabledModules.size > 0,
  });
  const invalidateWidgetData = widgetDataQuery.invalidate;

  const onEditWidget = useCallback(
    (
      instanceId: string,
      next: { params?: Record<string, unknown>; title?: string | undefined },
    ): void => {
      const item = layout.find((i) => i.instanceId === instanceId);
      const instance = page?.widgetInstances.find((i) => i.id === instanceId);
      if (!item || !instance) return;
      const selection = HOME_CONNECTION_SELECTION[item.kind];

      // 1. The approved-domain choice → the slot connection.
      let params = next.params;
      let writeConnection:
        | (() => Promise<DashboardRecord | undefined>)
        | undefined;
      if (params !== undefined && selection) {
        const presentation = { ...params };
        const selected: Record<string, unknown> = {};
        const previous: Record<string, unknown> = {};
        for (const key of selection.paramKeys) {
          selected[key] = params[key];
          previous[key] = item.params?.[key];
          delete presentation[key];
        }
        params = presentation;
        const selectedValue = selected[selection.paramKeys[0] as string];
        const previousValue = previous[selection.paramKeys[0] as string];
        const bothEmpty =
          selection.emptyRemovesConnection === true &&
          isEmptySelection(selectedValue) &&
          isEmptySelection(previousValue);
        if (!bothEmpty && !sameJson(selected, previous)) {
          // An emptied picker CLEARS the binding — suppressing the write
          // because the selection is empty makes a KPI choice un-undoable.
          writeConnection =
            selection.emptyRemovesConnection === true &&
            isEmptySelection(selectedValue)
              ? () => mutations.removeConnection(instanceId, selection.slotKey)
              : () =>
                  mutations.setConnection(
                    instanceId,
                    selection.slotKey,
                    selected,
                  );
        }
      }

      // 2. Presentation-only params + title → the instance PATCH. Both are
      // compared against the persisted instance so an unchanged draft (a KPI
      // swap on a kind with no editable params) never PATCHes an empty diff.
      const paramsChanged =
        params !== undefined && !sameJson(params, instance.params);
      const nextTitle = next.title ?? null;
      const titleChanged =
        "title" in next && nextTitle !== instance.titleOverride;

      void (async () => {
        if (writeConnection) {
          const saved = await writeConnection();
          // The failure is already surfaced; a second CAS write on top of a
          // now-stale version would only report a phantom conflict.
          if (!saved) return;
        }
        if (paramsChanged || titleChanged) {
          await mutations.onEditWidget(instanceId, {
            ...(paramsChanged ? { params } : {}),
            ...(titleChanged ? { title: nextTitle } : {}),
          });
        }
        // Selection changes keep the same widget IDs, so the saved data key
        // stays unchanged. Refresh it after the serialized writes settle.
        if (writeConnection) await invalidateWidgetData();
      })();
    },
    [layout, page, mutations, invalidateWidgetData],
  );

  const onLogMood = useCallback(
    (entry: MoodCheckWrite): void => {
      putMoodToday(entry)
        .then(() => invalidateWidgetData())
        .catch((error: unknown) => {
          // A rejected check-in must not read as a saved one: report it, then
          // refresh so the widget snaps back to the server's truth.
          onError?.(error);
          void invalidateWidgetData();
        });
    },
    [invalidateWidgetData, onError],
  );

  /**
   * The zone the SERVER derived this actor's current day in, read off the
   * resolved Mood Check slot.
   *
   * `null` until a Mood Check widget has resolved — which is correct: with no
   * Mood Check on the dashboard there is no dismissal to expire, so there is
   * nothing to schedule. A resolver that predates the field also yields
   * `null`, and the pre-dismissal behaviour (no timer) is the safe fallback.
   */
  const moodTimeZone = useMemo<string | null>(() => {
    const results = widgetDataQuery.data?.results;
    if (!results || !page) return null;
    for (const instance of page.widgetInstances) {
      if (instance.widgetKind !== "moodCheck") continue;
      const slot = results[instance.id]?.["mood"];
      if (slot?.status !== "ready") continue;
      const parsed = MoodTodayOutputSchema.safeParse(slot.data);
      if (parsed.success && parsed.data.timeZone) return parsed.data.timeZone;
    }
    return null;
  }, [page, widgetDataQuery.data]);

  const onSetMoodHidden = useCallback(
    (hidden: boolean): void => {
      // Identical failure posture to the check-in write, and for the same
      // reason: the widget applies the change optimistically, so a rejected
      // hide must be reported AND reverted to the server's truth rather than
      // left looking saved.
      putMoodVisibility(hidden)
        .then(() => invalidateWidgetData())
        .catch((error: unknown) => {
          onError?.(error);
          void invalidateWidgetData();
        });
    },
    [invalidateWidgetData, onError],
  );

  /**
   * Reveal Mood Check again at the actor's local midnight, on a page that
   * never reloads.
   *
   * Scheduled at the EXACT boundary rather than polled. The dismissal expires
   * when the server's derived local day changes, so a 15-minute poll would
   * either leave the widget hidden for up to fifteen minutes into the new day
   * or cost fifteen refetches an hour to avoid it. The zone comes from the
   * resolved mood dataset — the same zone the server derived the day in — so
   * the client never guesses the boundary from the browser's own zone, which
   * is precisely the guess PD1 removed.
   *
   * Tab focus and visibility stay, and they are not redundant: a suspended
   * laptop does not run timers, so waking the machine is the only signal that
   * a boundary was crossed while it slept.
   */
  useEffect(() => {
    if (!authed || moodTimeZone === null) return;
    let timer: number | undefined;
    let cancelled = false;
    let anchorDay = localDateIn(moodTimeZone, new Date());

    const schedule = (): void => {
      if (cancelled) return;
      timer = window.setTimeout(
        () => {
          if (cancelled) return;
          const current = localDateIn(moodTimeZone, new Date());
          if (current !== anchorDay) {
            anchorDay = current;
            void invalidateWidgetData();
          }
          schedule();
        },
        Math.min(msUntilNextLocalDay(moodTimeZone), MAX_TIMEOUT_MS),
      );
    };
    schedule();

    const refresh = (): void => {
      const current = localDateIn(moodTimeZone, new Date());
      anchorDay = current;
      void invalidateWidgetData();
      if (timer !== undefined) window.clearTimeout(timer);
      schedule();
    };
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", refresh);
    };
  }, [authed, invalidateWidgetData, moodTimeZone]);

  return {
    layout,
    isLoading: dashboard.query.isLoading,
    isError: dashboard.query.isError,
    retryDashboard: () => void dashboard.query.refetch(),
    record,
    page,
    widgetData: widgetDataQuery.data,
    widgetDataLoading: widgetDataQuery.isLoading,
    retryWidgetData: () => void widgetDataQuery.refetch(),
    // Keep the reorder promise so the shared grid can discard its transient
    // visual order when persistence rejects without publishing a new layout.
    onReorder: async (next) => {
      await mutations.onReorder(
        mergeHomeWidgetOrder(rawLayout, next) as DashboardLayoutItem[],
      );
    },
    onChangeWidth: (instanceId, width) =>
      void mutations.onChangeWidth(instanceId, width),
    onAddWidget: (kind) => {
      if (isHomeWidgetAvailable(kind, enabledModules)) {
        void mutations.onAddWidget(kind);
      }
    },
    onRemoveWidget: (instanceId) => void mutations.onRemoveWidget(instanceId),
    onEditWidget,
    onLogMood,
    onSetMoodHidden,
  };
}
